import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  Inject,
  ChangeDetectorRef
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { IAction } from '../../interfaces';
import { UIActionType } from '../../../insights/interfaces';
import { isMainButtonPressed } from '../../../utils/event';
import { ActionButtonType } from '../action-button/interfaces';
import { CheckBoxActionMenuSize, CheckBoxActionMenuType } from './interfaces';

@Component({
  selector: 'app-vi-check-box-action-menu',
  templateUrl: './check-box-action-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./check-box-action-menu.component.scss']
})
export class CheckBoxActionMenuComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) public actionList: IAction[] = [];
  @Input({ required: true }) public placeholder: string;
  @Input() public hasOpenerIcon = true;
  @Input() public isDisabled = false;
  @Input() public selectedAction: IAction;
  @Input() public hasTooltip = false;
  @Input() public labelForId = '';
  @Input() public icon: string;
  @Input() public type: CheckBoxActionMenuType = ActionButtonType.MAIN_ACTION_MENU;
  @Input() public size: CheckBoxActionMenuSize = CheckBoxActionMenuSize.Large;
  @Input() public uid?: string;
  @Input() public ariaLabelledBy?: string;
  @Input() public customPlaceholderFn: (initialValue: string, actions: IAction[]) => string;

  @Output() public selectedActionsChange = new EventEmitter<IAction[]>(true);

  public placeholderAction;
  public value = '';
  public expanded = false;
  public isEmptySelectedActions: boolean;
  public actions: IAction[] = [];
  public UIActionType = UIActionType;
  public ActionButtonType = ActionButtonType;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private timeout: any;
  private isActionButtonClick = false;
  private isCheckboxClick = false;
  private documentClickBound = false;
  private documentClickHandlerRef: (event: MouseEvent) => void;

  constructor(@Inject(DOCUMENT) private document: Document, private cdr: ChangeDetectorRef) {}

  public ngOnInit() {
    const placeholderText = this.customPlaceholderFn ? this.customPlaceholderFn(this.placeholder, this.actions) : this.placeholder;
    this.placeholderAction = { title: placeholderText, icon: this.icon };
    this.cdr.detectChanges();
    this.initActions();

    // Set selected menu actions according to input selected actions
    if (this.selectedAction) {
      this.setValue();
    } else {
      this.resetSelectedActions();
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    // If new actions list - set action
    if (changes && changes.actionList) {
      this.initActions();
      this.setValue();
    }
  }

  public ngOnDestroy(): void {
    this.unbindToDocumentClick();
  }

  public handleChange(action: IAction) {
    if (this.isEmptySelectedActions) {
      this.selectedAction = action;
      this.isEmptySelectedActions = false;
    }

    this.isCheckboxClick = true;
    this.updateSelectionByGroup(action);

    // Set new value for menu
    this.setValue();

    // Get selected actions
    const selectedActions = this.actions.filter(currentAction => currentAction.selected && currentAction.type !== UIActionType.GROUP);

    // Reset selected actions if no action is chosen
    if (selectedActions.length === 0) {
      this.resetSelectedActions();
    }

    this.selectedActionsChange.emit(selectedActions);
  }

  public toggleDropdown() {
    this.expanded = !this.expanded;
    this.isActionButtonClick = this.expanded;

    if (this.expanded) {
      this.bindToDocumentClick();
    }
  }

  private bindToDocumentClick() {
    if (!this.documentClickBound) {
      this.documentClickHandlerRef = this.documentClickHandler.bind(this);
      this.document.addEventListener('mouseup', this.documentClickHandlerRef);
      this.documentClickBound = true;
    }
  }

  private unbindToDocumentClick() {
    if (this.documentClickBound) {
      this.document.removeEventListener('mouseup', this.documentClickHandlerRef);
      this.documentClickBound = false;
    }
  }

  // Hide dropdown if user clicked on document area which is not current component area
  private collapse() {
    if (!this.isActionButtonClick && !this.isCheckboxClick) {
      this.expanded = false;
      this.unbindToDocumentClick();
      this.cdr.detectChanges();
    }
    this.isActionButtonClick = false;
    this.isCheckboxClick = false;
  }

  private documentClickHandler = (event: MouseEvent): void => {
    if (!isMainButtonPressed(event)) {
      return;
    }
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.collapse();
    });
  };

  private initActions() {
    // Simply copy the actions as-is, since group headers are now manually created
    // with UIActionType.GROUP in the parent component
    this.actions = [...this.actionList];
  }

  private updateSelectionByGroup(action: IAction) {
    // Groups are non-interactive, so we only handle individual item selection
    // No group logic needed since group headers are display-only
    if (action.type !== UIActionType.GROUP) {
      // Individual item selection - no additional logic needed
      // The selection state is already handled by the two-way binding
    }
  }

  private resetSelectedActions() {
    this.selectedAction = this.placeholderAction;
    this.value = this.placeholder;
    this.isEmptySelectedActions = true;
  }

  private setValue() {
    this.value = '';

    // If custom placeholder function is provided, use it to set the placeholder
    if (this.customPlaceholderFn) {
      this.value = this.customPlaceholderFn(this.placeholder, this.actions);
    } else {
      // If action is selected - add to value
      this.actions.forEach(action => {
        this.value += `${action.selected && action.type !== UIActionType.GROUP ? action.title + ', ' : ''}`;
      });

      // Remove last ' ,'
      if (this.value) {
        this.value = this.value.slice(0, -2);
      }
    }

    this.placeholderAction = { title: this.value ? this.value : this.placeholder, icon: this.icon };
    this.cdr.detectChanges();
  }
}
